import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BaseTransaction, Pagination, Response } from '@ui/src';
import { Fintrac, FintracTransaction } from '../../types';
import { fintracApi } from '../services';

export interface FintracReportState {
  incomingTransactions?: BaseTransaction[];
  incomingTransactionsPagination?: Pagination;
  report?: Fintrac;
  reportTransactions?: FintracTransaction[];
  reportTransactionsPagination?: Pagination;
  reportId?: string;
  selectedTransactions?: string[];
  errorsList?: string;
}

const initialState: FintracReportState = {};

export const FintracReportSlice = createSlice({
  name: 'fintracReport',
  initialState,
  reducers: {
    setSelectedTransactions: (state, action: PayloadAction<string[]>) => {
      state.selectedTransactions = action.payload;
    },
    setReportId: (state, action: PayloadAction<string>) => {
      state.reportId = action.payload;
    },
    clearReportDetailsState: (state) => {
      state.report = undefined;
      state.reportTransactions = undefined;
      state.reportTransactionsPagination = undefined;
      state.errorsList = undefined;
      state.reportId = undefined;
      state.incomingTransactions = undefined;
      state.incomingTransactionsPagination = undefined;
      state.selectedTransactions = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      fintracApi.endpoints.selectAllTransactionInReport.matchFulfilled,
      (state) => {
        if (state.reportId) {
          const nextTransactions = state.reportTransactions?.map((item) => {
            const result = { ...item };

            result.isSelectedForReport = true;

            return result;
          });

          state.selectedTransactions = nextTransactions?.map((item) => item.id);
          state.reportTransactions = nextTransactions;
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.deselectAllTransactionInReport.matchFulfilled,
      (state) => {
        if (state.reportId) {
          state.reportTransactions = state.reportTransactions?.map((item) => {
            const result = { ...item };

            result.isSelectedForReport = false;

            return result;
          });

          if (state.report) {
            state.report = {
              ...state.report,
              totalAmount: 0,
            };
          }

          state.selectedTransactions = [];
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReportTransactions.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReportTransactions.matchRejected,
      (state, action: any) => {
        if (state.reportId) {
          state.reportTransactions = undefined;
          state.reportTransactionsPagination = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReportTransactions.matchFulfilled,
      (state, action: PayloadAction<Response<FintracTransaction>>) => {
        if (state.reportId) {
          state.reportTransactions = action.payload.data.edges?.map(
            (item) => item.node,
          );

          state.reportTransactionsPagination = action.payload.data.pageInfo;

          const nextSelectedTransactions: string[] = [];

          action.payload.data.edges?.forEach((item) => {
            if (item.node.isSelectedForReport) {
              nextSelectedTransactions.push(item.node.id);
            }
          });

          state.selectedTransactions = nextSelectedTransactions;
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracReport.matchFulfilled,
      (state, action: PayloadAction<{ data: Fintrac }>) => {
        if (state.reportId) {
          state.report = action.payload.data;
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.submitReport.matchFulfilled,
      (state) => {
        if (state.reportId) {
          state.report = {
            ...state.report,
            status: 'SENT',
          } as Fintrac;
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.cancelReport.matchFulfilled,
      (state) => {
        if (state.reportId) {
          state.report = {
            ...state.report,
            status: 'CANCELLED',
          } as Fintrac;
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.selectTransactionInReport.matchFulfilled,
      (state, action: PayloadAction<{ data: FintracTransaction }>) => {
        if (state.reportId) {
          const nextReportTranscations = state?.reportTransactions?.map(
            (item) => {
              const result = { ...item };

              if (item.id === action.payload.data.id) {
                result.isSelectedForReport =
                  action.payload.data.isSelectedForReport;
              }

              return result;
            },
          );

          state.reportTransactions = nextReportTranscations;

          state.selectedTransactions = nextReportTranscations
            ?.filter((item) => item.isSelectedForReport)
            ?.map((item) => item.id);

          if (state.report) {
            let amount = state.report.totalAmount;

            if (action.payload.data.isSelectedForReport) {
              amount += action.payload.data.convertedAmount;
            } else {
              amount -= action.payload.data.convertedAmount;
            }

            state.report = {
              ...state.report,
              totalAmount: amount,
            };
          }
        }
      },
    );
    builder.addMatcher(
      fintracApi.endpoints.getFintracIncomingTransactions.matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        if (state.reportId) {
          state.incomingTransactions = action.payload.data?.edges?.map(
            (item) => ({
              ...item.node,
              url: `/transactions/incoming/${item.node.id}`,
            }),
          );
          state.incomingTransactionsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const { clearReportDetailsState, setSelectedTransactions, setReportId } =
  FintracReportSlice.actions;

export default FintracReportSlice.reducer;
